import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_ADMIN_COUNTRY_CODES_LIST, API_ADMIN_COUNTRY_CODES_CREATE,
  API_ADMIN_COUNTRY_CODES_GET, API_ADMIN_COUNTRY_CODES_UPDATE,
  API_ADMIN_COUNTRY_CODES_DELETE, API_ADMIN_COUNTRY_CODES_ACTIVATE,
  API_ADMIN_COUNTRY_CODES_DEACTIVATE, API_COUNTRY_CODES_ACTIVE,
} from '../../constants/apiConstants';

// ============================================================================
// Types
// ============================================================================

export interface CountryCode {
  id: number;
  alpha2Code: string;
  alpha3Code: string;
  numericCode: string;
  countryName: string;
  shortName: string;
  sortOrder: number;
  isActive: boolean;
  status: string;
  // Country Metadata Enrichment — 9 new fields
  continentCode: string;
  continentName: string;
  region: string | null;
  capital: string | null;
  capitalLatitude: number | null;
  capitalLongitude: number | null;
  currencyCode: string | null;
  phoneCode: string | null;
  flagEmoji: string;
}

export interface CreateCountryCodeRequest {
  alpha2Code: string;
  alpha3Code?: string;
  numericCode?: string;
  countryName: string;
  shortName: string;
  sortOrder?: number;
  // Country Metadata Enrichment — 9 new fields
  continentCode?: string;
  continentName?: string;
  region?: string | null;
  capital?: string | null;
  capitalLatitude?: number | null;
  capitalLongitude?: number | null;
  currencyCode?: string | null;
  phoneCode?: string | null;
  flagEmoji?: string;
}

export interface UpdateCountryCodeRequest {
  countryName: string;
  shortName: string;
  sortOrder?: number;
  // Country Metadata Enrichment — 9 new fields
  continentCode?: string;
  continentName?: string;
  region?: string | null;
  capital?: string | null;
  capitalLatitude?: number | null;
  capitalLongitude?: number | null;
  currencyCode?: string | null;
  phoneCode?: string | null;
  flagEmoji?: string;
}

export interface CountryCodeListFilters {
  searchText?: string;
  status?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: string;
  // Country Metadata Enrichment — 4 filter params
  continentCode?: string;
  region?: string;
  currencyCode?: string;
  search?: string;
}

/**
 * Continent code allow-list — matches backend CodeListsCodes.ContinentCode.
 */
export const CONTINENT_CODES = ['AS', 'EU', 'AF', 'NA', 'SA', 'OC', 'AN'] as const;
export type ContinentCode = typeof CONTINENT_CODES[number];

// ============================================================================
// Country Code Service
// ============================================================================

export const countryCodeService = {
  /**
   * Fetches paginated country codes for admin management.
   */
  async getCountryCodes(filters: CountryCodeListFilters = {}) {
    const {
      searchText, status, pageNumber = 1, pageSize = 20, sortBy, sortDirection,
      continentCode, region, currencyCode, search,
    } = filters;
    try {
      const response = await apiClient.get(API_ADMIN_COUNTRY_CODES_LIST, {
        params: {
          searchText, status, pageNumber, pageSize, sortBy, sortDirection,
          continentCode, region, currencyCode, search,
        },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      const data = response.data.data;
      return { countryCodes: data?.countries ?? [], totalCount: data?.totalCount ?? 0, pageNumber: data?.pageNumber ?? 1, pageSize: data?.pageSize ?? 20 };
    } catch (error) {
      logger.error('Failed to fetch country codes', error);
      throw error;
    }
  },

  /**
   * Fetches active country codes for dropdown consumption.
   */
  async getActiveCountryCodes(): Promise<CountryCode[]> {
    try {
      const response = await apiClient.get(API_COUNTRY_CODES_ACTIVE);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.countries ?? [];
    } catch (error) {
      logger.error('Failed to fetch active country codes', error);
      throw error;
    }
  },

  /**
   * Fetches a single country code by ID.
   */
  async getCountryCodeById(id: number): Promise<CountryCode> {
    try {
      const response = await apiClient.get(API_ADMIN_COUNTRY_CODES_GET(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data?.countryCode;
    } catch (error) {
      logger.error('Failed to fetch country code', error);
      throw error;
    }
  },

  /**
   * Creates a new country code entry.
   */
  async createCountryCode(request: CreateCountryCodeRequest) {
    try {
      const response = await apiClient.post(API_ADMIN_COUNTRY_CODES_CREATE, request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Country code created successfully');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to create country code: ${msg}`);
      throw error;
    }
  },

  /**
   * Updates an existing country code entry.
   */
  async updateCountryCode(id: number, request: UpdateCountryCodeRequest) {
    try {
      const response = await apiClient.put(API_ADMIN_COUNTRY_CODES_UPDATE(id), request);
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Country code updated successfully');
      return response.data.data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to update country code: ${msg}`);
      throw error;
    }
  },

  /**
   * Soft-deletes a country code entry.
   */
  async deleteCountryCode(id: number) {
    try {
      const response = await apiClient.delete(API_ADMIN_COUNTRY_CODES_DELETE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Country code deleted successfully');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to delete country code: ${msg}`);
      throw error;
    }
  },

  /**
   * Activates a country code entry.
   */
  async activateCountryCode(id: number) {
    try {
      const response = await apiClient.put(API_ADMIN_COUNTRY_CODES_ACTIVATE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Country code activated');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to activate country code: ${msg}`);
      throw error;
    }
  },

  /**
   * Deactivates a country code entry.
   */
  async deactivateCountryCode(id: number) {
    try {
      const response = await apiClient.put(API_ADMIN_COUNTRY_CODES_DEACTIVATE(id));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Country code deactivated');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to deactivate country code: ${msg}`);
      throw error;
    }
  },
};
